import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Ledger, LedgerType } from '../../types/ledger';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';
import { useAuth } from '../../context/AuthContext';
import { getCurrencyFromPhone } from '../../data/countries';

const TYPE_PALETTE: Record<
  LedgerType | 'default',
  { lightBg: string; darkBg: string; lightText: string; darkText: string }
> = {
  Business: {
    lightBg: '#E3E9FF',
    darkBg: '#1B2144',
    lightText: '#1C3FAA',
    darkText: '#A5BBFF',
  },
  Personal: {
    lightBg: '#E5F7EE',
    darkBg: '#10261B',
    lightText: '#0F5132',
    darkText: '#84F0B7',
  },
  Academics: {
    lightBg: '#FFF3E0',
    darkBg: '#2B1E05',
    lightText: '#B56004',
    darkText: '#F7C989',
  },
  Social: {
    lightBg: '#FFE6EA',
    darkBg: '#2B1217',
    lightText: '#B42334',
    darkText: '#FFA6AF',
  },
  default: {
    lightBg: '#EEEFF2',
    darkBg: '#1F1F1F',
    lightText: '#374151',
    darkText: '#D1D5DB',
  },
};

const getTypeAppearance = (type: LedgerType | undefined, isDark: boolean) => {
  const palette = type ? TYPE_PALETTE[type] : TYPE_PALETTE.default;
  return {
    background: isDark ? palette.darkBg : palette.lightBg,
    text: isDark ? palette.darkText : palette.lightText,
  };
};

interface LedgerAccountCardProps {
  ledger: Ledger;
  onPress: () => void;
  onBookmarkToggle?: () => void;
}

const LedgerAccountCard: React.FC<LedgerAccountCardProps> = ({
  ledger,
  onPress,
  onBookmarkToggle,
}) => {
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const currency = getCurrencyFromPhone(user?.phoneNumber);
  const isPositiveBalance = ledger.balance >= 0;
  const { background: typeBadgeBackground, text: typeBadgeText } = getTypeAppearance(
    ledger.type,
    isDark
  );
  const gradientColors: readonly [string, string, string] = isDark
    ? ['rgba(255,255,255,0.55)', 'rgba(120,120,120,0.35)', 'rgba(235,235,235,0.45)']
    : ['rgba(255,255,255,0.85)', 'rgba(185,185,185,0.55)', 'rgba(245,245,245,0.8)'];

  return (
    <TouchableOpacity
      style={[styles.cardWrapper, { shadowColor: theme.shadow }]}
      onPress={onPress}
      activeOpacity={0.85}
    >
      <LinearGradient
        colors={gradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.cardBorder}
      >
        <View
          style={[
            styles.card,
            {
              backgroundColor: theme.card,
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          ]}
        >
          {ledger.type && (
            <View
              style={[
                styles.typeBadge,
                {
                  backgroundColor: typeBadgeBackground,
                  borderColor: typeBadgeBackground,
                },
              ]}
            >
              <View style={[styles.typeDot, { backgroundColor: typeBadgeText }]} />
              <ThemedText style={[styles.typeText, { color: typeBadgeText }]}>
                {ledger.type}
              </ThemedText>
            </View>
          )}

          <View style={styles.content}>
            <View style={styles.header}>
              <View style={styles.nameContainer}>
                <ThemedText style={[styles.personName, { color: theme.text }]} numberOfLines={1}>
                  {ledger.personName}
                </ThemedText>
                <ThemedText
                  style={[styles.personMeta, { color: theme.textSecondary }]}
                  numberOfLines={1}
                >
                  {ledger.isShared ? 'Shared ledger' : 'Private ledger'}
                </ThemedText>
              </View>
              <View style={styles.headerActions}>
                {ledger.isShared && ledger.linkStatus === 'accepted' && (
                  <View style={[styles.sharedBadge, { backgroundColor: theme.accent + '20' }]}>
                    <Ionicons name="link" size={12} color={theme.accent} />
                    <ThemedText style={[styles.sharedText, { color: theme.accent }]}>
                      {ledger.isViewOnly ? 'View Only' : 'Shared'}
                    </ThemedText>
                  </View>
                )}
                {onBookmarkToggle && (
                  <TouchableOpacity
                    onPress={(e) => {
                      e.stopPropagation();
                      onBookmarkToggle();
                    }}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons
                      name={ledger.isBookmarked ? 'bookmark' : 'bookmark-outline'}
                      size={20}
                      color={ledger.isBookmarked ? theme.accent : theme.textSecondary}
                    />
                  </TouchableOpacity>
                )}
              </View>
            </View>

            <View style={styles.summaryRow}>
              <View style={styles.flowColumn}>
                <View style={styles.flowItem}>
                  <View style={[styles.flowIcon, styles.receivedIcon]}>
                    <Ionicons name="arrow-down" size={12} color="#0A8A4A" />
                  </View>
                  <View>
                    <ThemedText style={[styles.flowLabel, { color: theme.textSecondary }]}>
                      Received
                    </ThemedText>
                    <ThemedText style={[styles.flowAmount, { color: theme.text }]}>
                      {currency.symbol} {ledger.totalReceived.toLocaleString('en-IN')}
                    </ThemedText>
                  </View>
                </View>

                <View style={styles.flowItem}>
                  <View style={[styles.flowIcon, styles.paidIcon]}>
                    <Ionicons name="arrow-up" size={12} color="#D32F2F" />
                  </View>
                  <View>
                    <ThemedText style={[styles.flowLabel, { color: theme.textSecondary }]}>
                      Paid
                    </ThemedText>
                    <ThemedText style={[styles.flowAmount, { color: theme.text }]}>
                      {currency.symbol} {ledger.totalPaid.toLocaleString('en-IN')}
                    </ThemedText>
                  </View>
                </View>
              </View>

              <View style={[styles.groupDivider, { backgroundColor: theme.border }]} />

              <View
                style={[
                  styles.balanceBlock,
                ]}
              >
                <ThemedText style={[styles.balanceLabel, { color: theme.textSecondary }]}>
                  Balance
                </ThemedText>
                <ThemedText
                  style={[
                    styles.balanceValue,
                    isPositiveBalance ? styles.positiveBalance : styles.negativeBalance,
                  ]}
                >
                  {isPositiveBalance ? '+ ' : '- '}{currency.symbol} {Math.abs(ledger.balance).toLocaleString('en-IN')}
                </ThemedText>
                <ThemedText style={[styles.balanceHint, { color: theme.textSecondary }]}>
                  {isPositiveBalance ? 'They owe us' : 'We owe them'}
                </ThemedText>
              </View>
            </View>
          </View>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 20,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 5,
  },
  cardBorder: {
    borderRadius: 20,
    padding: 1,
  },
  card: {
    position: 'relative',
    padding: 20,
    paddingTop: 36,
    borderRadius: 18,
    borderWidth: 1,
    overflow: 'hidden',
  },
  typeBadge: {
    position: 'absolute',
    top: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderBottomLeftRadius: 18,
    borderTopRightRadius: 18,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
    gap: 6,
  },
  typeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  typeText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  content: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sharedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  sharedText: {
    fontSize: 11,
    fontWeight: '700',
  },
  nameContainer: {
    flex: 1,
    marginRight: 8,
  },
  personName: {
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: 0.3,
  },
  personMeta: {
    fontSize: 12,
    marginTop: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    gap: 16,
  },
  flowColumn: {
    width: 100,
    gap: 10,
  },
  flowItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  flowIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    justifyContent: 'center',
    alignItems: 'center',
  },
  flowLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  flowAmount: {
    fontSize: 16,
    fontWeight: '700',
  },
  balanceBlock: {
    flex: 1,
    borderRadius: 18,
    paddingHorizontal: 0,
    paddingVertical: 0,
    justifyContent: 'center',
    gap: 10,
  },
  balanceLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  balanceValue: {
    fontSize: 32,
    fontWeight: '800',
    letterSpacing: 0.5,
  },
  balanceHint: {
    fontSize: 12,
    fontWeight: '600',
  },
  positiveBalance: {
    color: '#11A45E',
  },
  negativeBalance: {
    color: '#E14B4B',
  },
  receivedIcon: {
    backgroundColor: '#0A8A4A20',
  },
  paidIcon: {
    backgroundColor: '#D32F2F20',
  },
  groupDivider: {
    width: 1,
    marginHorizontal: 4,
    borderRadius: 999,
  },
});

export default LedgerAccountCard;
